<template>
  <AppShell :nav-items="navItems" :account-nav-items="accountNav" :admin-nav-items="adminNav" :class="lockClasses" :style="lockChips">
    <template #before-page>
      <ToastHost />
      <ConfirmHost />
      <UpgradeHost />
      <div v-if="memberPage && (overLimit || showSwitcher || pausedBrand)" class="pb-topbar">
        <div v-if="overLimit" class="pb-alert pb-over" role="status">
          <i class="mdi mdi-alert-outline pb-ico" aria-hidden="true"></i>
          <div class="pb-over-text">
            <p>{{ overLimitText }}</p>
            <div class="pb-actions">
              <router-link to="/settings#plan" class="pb-button sm outline">Review</router-link>
              <router-link v-if="isOwner" to="/subscribe" class="pb-button sm">See plans</router-link>
            </div>
          </div>
        </div>
        <div v-if="pausedBrand" class="pb-alert is-error" role="status">
          <i class="mdi mdi-pause-octagon-outline pb-ico" aria-hidden="true"></i>
          <p>{{ possessive(pausedBrand.name) }} public pages are paused by a TrustClap administrator<span v-if="pausedBrand.admin_reason">: {{ pausedBrand.admin_reason }}</span>
            Your feedback and exports are unaffected. Contact support if you think this is a mistake.</p>
        </div>
        <BrandSwitcher v-if="showSwitcher" :allow-all="inboxPage" :all="inboxAll" @all="showAllBrands" @change="leaveAllBrands" />
      </div>
    </template>
  </AppShell>
</template>

<script>
import AppShell from '@shared/components/AppShell.vue';
import BrandSwitcher from './components/BrandSwitcher.vue';
import ConfirmHost from './components/ConfirmHost.vue';
import ToastHost from './components/ToastHost.vue';
import UpgradeHost from './components/UpgradeHost.vue';
import { possessive } from './lib/format';
import { planLabel } from './lib/plans';
import { overLimitText as describeOverLimit } from './lib/overLimit';

const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/logout'];

export default {
  name: 'TrustClapApp',
  components: { AppShell, BrandSwitcher, ConfirmHost, ToastHost, UpgradeHost },
  methods: {
    possessive,
    showAllBrands() {
      this.$router.replace({ path: this.$route.path, query: Object.assign({}, this.$route.query, { brand: 'all' }) }).catch(() => {});
    },
    // Picking one brand in the switcher leaves the all-brands inbox for that brand.
    leaveAllBrands() {
      if (!this.inboxAll) return;
      const query = Object.assign({}, this.$route.query);
      delete query.brand;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    }
  },
  computed: {
    data() { return this.$store.getters['praiseboard/data']; },
    plan() { return this.$store.getters['praiseboard/plan']; },
    limits() { return this.$store.getters['praiseboard/limits']; },
    isOwner() { return this.$store.getters['praiseboard/isOwner']; },
    brands() { return this.$store.getters['praiseboard/activeBrands']; },
    hasWorkspace() { return this.$store.getters['praiseboard/hasWorkspace']; },
    memberPage() {
      if (!this.$store.getters.isAuthenticated) return false;
      if (this.$route.matched.some(record => record.meta && record.meta.hideAppChrome)) return false;
      return !AUTH_PATHS.some(path => this.$route.path === path || this.$route.path.indexOf(path + '/') === 0);
    },
    showSwitcher() {
      return this.brands.length > 1 && this.$route.matched.some(record => record.meta && record.meta.brand);
    },
    pausedBrand() {
      const brand = this.$store.getters['praiseboard/brand'];
      return brand && brand.admin_paused && this.$route.matched.some(record => record.meta && record.meta.brand) ? brand : null;
    },
    newCount() {
      return (this.brands || []).reduce((sum, brand) => sum + ((brand.counts && brand.counts.new) || 0), 0);
    },
    // The inbox can list every brand at once ("All brands" in the switcher, `brand=all` in the address).
    inboxPage() { return this.$route.path === '/inbox' || this.$route.path.indexOf('/inbox/') === 0; },
    inboxAll() { return this.inboxPage && this.$route.query.brand === 'all'; },
    // "Inbox (N new)" counts every brand, so with several brands it opens the inbox on all of them.
    inboxLink() { return this.brands.length > 1 && this.newCount ? '/inbox?status=new&brand=all' : '/inbox'; },
    navItems() {
      const items = [{ title: 'Overview', icon: 'mdi-view-dashboard-outline', to: '/' }];
      if (!this.hasWorkspace) return items.concat([{ title: 'Help', icon: 'mdi-help-circle-outline', to: '/help' }]);
      // Brands and Team & approvals are always listed: on a plan that can't use them yet they show a lock and the plan
      // that includes them, and the screen shows what it would do.
      items.push({ title: 'Brands', icon: this.navLocks.brands ? 'mdi-lock-outline' : 'mdi-domain', to: '/brands' });
      items.push(
        { title: 'Collection forms', icon: 'mdi-form-select', to: '/forms' },
        { title: this.newCount ? 'Inbox (' + this.newCount + ' new)' : 'Inbox', icon: 'mdi-inbox-outline', to: this.inboxLink },
        { title: 'Testimonials', icon: 'mdi-comment-quote-outline', to: '/testimonials' },
        { title: 'Widgets', icon: 'mdi-widgets-outline', to: '/widgets' },
        { title: 'Hosted wall', icon: 'mdi-view-quilt-outline', to: '/wall' },
        { title: 'Quote graphics', icon: 'mdi-image-text', to: '/graphics' },
        { title: 'Reports', icon: 'mdi-chart-box-outline', to: '/reports' }
      );
      items.push({ title: 'Team & approvals', icon: this.navLocks.team ? 'mdi-lock-outline' : 'mdi-account-group-outline', to: '/team' });
      // One quiet way to see what each plan adds, for the Free plan only.
      if (this.plan === 'free') items.push({ title: 'Compare plans', icon: 'mdi-compare-horizontal', to: '/plans' });
      items.push({ title: 'Settings', icon: 'mdi-cog-outline', to: '/settings' },
        { title: 'Help', icon: 'mdi-help-circle-outline', to: '/help' });
      return items;
    },
    accountNav() {
      if (this.hasWorkspace && !this.isOwner) return [];
      return [
        { icon: 'mdi-card-account-details-outline', title: 'Subscription', to: '/subscription' },
        { icon: 'mdi-credit-card-outline', title: 'Billing', to: '/billing' }
      ];
    },
    adminNav() { return [{ icon: 'mdi-shield-check-outline', title: 'Brand moderation', to: '/admin/praiseboard' }]; },
    // The plan that includes a menu item's screen, for items the workspace's plan can't use yet: Brands until the plan
    // runs more than one brand, Team & approvals until it has a second seat or a client feature.
    navLocks() {
      if (!this.hasWorkspace) return {};
      const availableOn = this.$store.getters['praiseboard/availableOn'];
      const limits = this.limits;
      return {
        brands: (limits.brands || 1) <= 1 ? availableOn('brands') : null,
        team: (limits.seats || 1) <= 1 && !limits.client_approvals && !limits.client_reports ? availableOn('seats') : null
      };
    },
    // AppShell draws only an icon, a title and a link, so the chip is drawn by CSS on that link (style block below).
    lockClasses() { return Object.keys(this.navLocks).filter(key => this.navLocks[key]).map(key => 'pb-nav-lock-' + key); },
    lockChips() {
      const matrix = this.$store.getters['praiseboard/planMatrix'];
      const chips = {};
      Object.keys(this.navLocks).forEach(key => {
        const name = this.navLocks[key] ? planLabel(matrix, this.navLocks[key]) : '';
        if (name) chips['--pb-nav-lock-' + key] = '"' + name + '" / "Available on ' + name + '"';
      });
      return chips;
    },
    overLimit() { return this.$store.getters['praiseboard/overLimit']; },
    overLimitText() { return describeOverLimit(this.overLimit, this.data && this.data.plan_label); }
  }
};
</script>

<style>
.pb-topbar{max-width:1240px;margin:0 auto;padding:20px 40px 0;display:flex;flex-direction:column;align-items:flex-start;gap:12px}
.pb-topbar .pb-alert{margin:0;width:100%}
.pb-over-text{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:10px 16px;flex:1}
.pb-over-text p{margin:0;flex:1 1 420px}
@media(max-width:960px){.pb-topbar{padding:16px 22px 0}}
@media(max-width:600px){.pb-topbar{padding:14px 16px 0}}
/* The plan chip of a locked menu item (navLocks), after its title: the title wraps rather than hide the chip. */
.pb-nav-lock-brands .side-menu-nav a.v-list-item[href="/brands"] .v-list-item__title,
.pb-nav-lock-team .side-menu-nav a.v-list-item[href="/team"] .v-list-item__title{white-space:normal;overflow:visible;line-height:1.35}
.pb-nav-lock-brands .side-menu-nav a.v-list-item[href="/brands"] .v-list-item__title::after,
.pb-nav-lock-team .side-menu-nav a.v-list-item[href="/team"] .v-list-item__title::after{display:inline-block;margin-left:6px;vertical-align:1px;
  padding:0 7px;border-radius:999px;background:#FFFFFF;color:#7C2F1B;border:1px solid rgba(180,71,43,.30);font:700 11px/1.6 var(--font-body);letter-spacing:0}
.pb-nav-lock-brands .side-menu-nav a.v-list-item[href="/brands"] .v-list-item__title::after{content:var(--pb-nav-lock-brands)}
.pb-nav-lock-team .side-menu-nav a.v-list-item[href="/team"] .v-list-item__title::after{content:var(--pb-nav-lock-team)}
</style>
